import React, { useState, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Upload, X, Camera, Image as ImageIcon } from 'lucide-react';

const PhotosStep = ({ formData, updateFormData }) => {
  const primaryInputRef = useRef(null);
  const additionalInputRef = useRef(null);
  
  const handlePrimaryPhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateFormData({ primary_photo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleAdditionalPhotosUpload = (e) => {
    const files = Array.from(e.target.files);
    const currentPhotos = formData.additional_photos || [];
    
    if (currentPhotos.length + files.length > 5) {
      alert('You can upload a maximum of 5 additional photos');
      return;
    }
    
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateFormData({
          additional_photos: [...(formData.additional_photos || []), reader.result]
        });
      };
      reader.readAsDataURL(file);
    });
  };
  
  const removeAdditionalPhoto = (index) => {
    const updatedPhotos = formData.additional_photos.filter((_, i) => i !== index);
    updateFormData({ additional_photos: updatedPhotos });
  };
  
  return (
    <div className="space-y-6">
      <p className="text-neutral-600 mb-6">
        Add photos to make your profile stand out
      </p>
      
      {/* Primary Photo */}
      <div className="space-y-3">
        <Label>Profile Photo *</Label>
        <p className="text-sm text-neutral-500">This will be your main photo</p>
        
        <div className="flex items-start gap-4">
          {/* Photo Preview or Upload Area */}
          <div
            onClick={() => primaryInputRef.current?.click()}
            className="relative w-48 h-48 border-2 border-dashed border-neutral-300 rounded-xl flex items-center justify-center cursor-pointer hover:border-primary-500 transition-all bg-neutral-50 overflow-hidden"
          >
            {formData.primary_photo ? (
              <>
                <img
                  src={formData.primary_photo}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all flex items-center justify-center">
                  <Camera className="h-8 w-8 text-white opacity-0 hover:opacity-100 transition-all" />
                </div>
              </>
            ) : (
              <div className="text-center p-4">
                <Upload className="h-10 w-10 text-neutral-400 mx-auto mb-2" />
                <p className="text-sm text-neutral-600 font-medium">Click to upload</p>
                <p className="text-xs text-neutral-500 mt-1">JPG, PNG up to 10MB</p>
              </div>
            )}
          </div>
          
          <input
            ref={primaryInputRef}
            type="file"
            accept="image/*"
            onChange={handlePrimaryPhotoUpload}
            className="hidden"
          />
          
          <div className="flex-1">
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <h4 className="font-semibold text-primary-900 mb-2">Photo Tips</h4>
              <ul className="text-sm text-primary-700 space-y-1">
                <li>• Use a clear, recent photo</li>
                <li>• Show your face clearly</li>
                <li>• Smile naturally</li>
                <li>• Avoid group photos</li>
                <li>• Good lighting works wonders</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      {/* Additional Photos */}
      <div className="space-y-3">
        <Label>Additional Photos (Optional)</Label>
        <p className="text-sm text-neutral-500">Add up to 5 more photos (travel, hobbies, lifestyle)</p>
        
        <div className="grid grid-cols-3 gap-4">
          {/* Existing Photos */}
          {formData.additional_photos?.map((photo, index) => (
            <div key={index} className="relative aspect-square rounded-lg overflow-hidden border-2 border-neutral-200">
              <img
                src={photo}
                alt={`Additional ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => removeAdditionalPhoto(index)}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-all"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          
          {/* Upload Button */}
          {(!formData.additional_photos || formData.additional_photos.length < 5) && (
            <div
              onClick={() => additionalInputRef.current?.click()}
              className="aspect-square border-2 border-dashed border-neutral-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-primary-500 transition-all bg-neutral-50"
            >
              <div className="text-center">
                <ImageIcon className="h-8 w-8 text-neutral-400 mx-auto mb-2" />
                <p className="text-xs text-neutral-600 font-medium">Add Photo</p>
              </div>
            </div>
          )}
          
          <input
            ref={additionalInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleAdditionalPhotosUpload}
            className="hidden"
          />
        </div>
      </div>
      
      {/* Photo Guidelines */}
      <div className="bg-neutral-100 rounded-lg p-4">
        <p className="text-sm text-neutral-700">
          <strong>Note:</strong> All photos are reviewed to ensure they meet our community guidelines.
          Photos showing inappropriate content, nudity, or copyrighted material will be removed.
        </p>
      </div>
    </div>
  );
};

export default PhotosStep;
